// Migration utility for renaming searchApiUrl -> searchUrl and adding platform field
// Follows the same pattern as propertyMigration.js

import { writeBatch, doc, deleteField } from 'firebase/firestore';
import { db, appId } from '../config/firebase';
import { detectSearchPlatform, unwrapProxySearchUrl } from './propertyPlatforms';
import { PLATFORMS } from './propertyPlatforms';

/**
 * Migrate existing tracked searches to the new schema:
 *   - Rename searchApiUrl -> searchUrl
 *   - Add platform field (detected from URL, defaults to 'bazaraki')
 *   - Remove old searchApiUrl field
 *
 * @param {string} userId - User ID
 * @param {Array} searches - Array of existing tracked search documents
 * @returns {Promise<number>} - Number of searches migrated
 */
export const migrateSearchesToNewSchema = async (userId, searches) => {
  if (!db || !userId || !searches.length) {
    return 0;
  }

  const batch = writeBatch(db);
  let count = 0;

  for (const search of searches) {
    // Only migrate documents that have the old field but not the new one
    if (search.searchApiUrl && !search.searchUrl) {
      const ref = doc(
        db,
        `artifacts/${appId}/users/${userId}/trackedSearches`,
        search.id
      );

      batch.update(ref, {
        searchUrl: search.searchApiUrl,
        platform: detectSearchPlatform(search.searchApiUrl) || PLATFORMS.BAZARAKI,
        searchApiUrl: deleteField(),
      });

      count++;
    }
  }

  if (count > 0) {
    try {
      await batch.commit();
      console.log(`Successfully migrated ${count} tracked searches to new schema`);
    } catch (error) {
      console.error('Error migrating tracked searches:', error);
      throw error;
    }
  }

  return count;
};

/**
 * Check if any tracked searches need migration
 * @param {Array} searches - Array of tracked search documents
 * @returns {boolean} - True if migration is needed
 */
export const needsSearchMigration = (searches) => {
  return searches.some((s) => s.searchApiUrl && !s.searchUrl);
};

/**
 * Fix searches that have a proxy URL stored instead of the real target URL
 * (e.g. http://localhost:8787/?url=... from pasting while on localhost)
 *
 * @param {string} userId - User ID
 * @param {Array} searches - Array of existing tracked search documents
 * @returns {Promise<number>} - Number of searches fixed
 */
export const migrateProxySearchUrls = async (userId, searches) => {
  if (!db || !userId || !searches.length) return 0;

  const batch = writeBatch(db);
  let count = 0;

  for (const search of searches) {
    const url = search.searchUrl;
    if (!url) continue;
    const unwrapped = unwrapProxySearchUrl(url);
    if (unwrapped !== url) {
      const ref = doc(
        db,
        `artifacts/${appId}/users/${userId}/trackedSearches`,
        search.id
      );
      batch.update(ref, { searchUrl: unwrapped });
      count++;
    }
  }

  if (count > 0) {
    try {
      await batch.commit();
      console.log(`Fixed ${count} tracked search URLs (unwrapped proxy URLs)`);
    } catch (error) {
      console.error('Error fixing proxy search URLs:', error);
      throw error;
    }
  }

  return count;
};

/**
 * Check if any searches have a proxy URL that should be unwrapped
 */
export const needsProxyUrlMigration = (searches) => {
  return searches.some((s) => s.searchUrl && unwrapProxySearchUrl(s.searchUrl) !== s.searchUrl);
};
